/*
 * @Author: Json Chen
 * @Date: 2021-07-20 16:13:45
 * @LastEditTime: 2022-06-06 08:51:23
 * @LastEditors: Json Chen
 * @Description:
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Settings/Localisation/index.tsx
 */

import CommonHeader from '@/src/components/CommonHeader';
import AddressFormats from '@/src/pages/Settings/Localisation/Components/AddressFormats';
import CommonFormItem from '@/src/pages/Settings/Localisation/Components/CommonFormItem';
import {
  getLocalisation,
  setLocalisation,
} from '@/src/request/settings/localisation';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { useRequest } from 'ahooks';
import { Checkbox, Form, Input, Spin, message } from 'antd';
import React, { useEffect } from 'react'; // useState
import { useHistory } from 'react-router';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import { breadcrumbData } from './config';
import styles from './style.module.less';
import emit from 'src/tools/event';
import { UPDATE_STORE_SETTING } from 'src/tools/constant';
const permissions = () => {
  const view_settings = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsLocalisation,
        operation: EnumOperation.view_settings,
      }),
    ],
  });
  const edit_setting = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsLocalisation,
        operation: EnumOperation.edit_setting,
      }),
    ],
  });
  return {
    view_settings,
    edit_setting,
  };
};

const Localisation = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { view_settings, edit_setting } = permissions();
  const history = useHistory();
  const [form] = Form.useForm();

  const [onSaveDisable, setOnSaveDisable] = React.useState<boolean>(true);
  const [timezoneOptions, setTimezoneOptions] = React.useState([]);
  const [localisationSettingUuid, setLocalisationSettingUuid] =
    React.useState('');

  const { loading: setLocalisationLoading, run: _setLocalisation } = useRequest(
    (params) => setLocalisation(params),
    {
      manual: true,
      ready: !!edit_setting,
      onSuccess(result) {
        message.success(getI18n('general.savesuccess'));
        history.push('/settings');
        emit.emit(UPDATE_STORE_SETTING);
      },
    },
  );

  const { loading: getLocalisationLoading, run: _getLocalisation } = useRequest(
    getLocalisation,
    {
      manual: true,
      ready: !!view_settings,
      onSuccess(result) {
        if (result && result.localisationSetting && result.timezones) {
          const { timezones, localisationSetting } = result;
          const {
            uuid,
            timezone,
            dateFormat,
            timeFormat,
            decimalSeparator,
            thousandsSeparator,
            addressFormats,
            googleAutoCompleteEnabled,
            googlePlaceKey,
          } = localisationSetting;

          setTimezoneOptions(timezones);
          setLocalisationSettingUuid(uuid);

          form.setFieldsValue({
            timezone,
            dateFormat,
            timeFormat,
            decimalSeparator,
            thousandsSeparator,
            addressFormats,
            googleAutoCompleteEnabled,
            googlePlaceKey,
          });
        }
      },
    },
  );

  const onValuesChange = () => {
    if (onSaveDisable) {
      setOnSaveDisable(!onSaveDisable);
    }
  };

  const onFormSubmit = () => {
    form.validateFields().then((res) => {
      const params = {
        uuid: localisationSettingUuid,
        ...res,
      };
      _setLocalisation(params);
    });
  };

  const onGoBackClick = () => {
    history.push(`/settings`);
  };

  useEffect(() => {
    _getLocalisation();
  }, []);

  return (
    <div className={styles.container}>
      <Spin spinning={getLocalisationLoading || setLocalisationLoading}>
        <Form
          name={'localisation'}
          onValuesChange={onValuesChange}
          form={form}
          colon={false}
          layout={'vertical'}
          labelCol={{
            span: 8,
          }}
          wrapperCol={{
            span: 24,
          }}
          className={styles.editForm}>
          <CommonHeader
            breadcrumbData={breadcrumbData({ getI18n })}
            onSaveDisable={onSaveDisable}
            onSubmit={onFormSubmit}
            onDiscard={onGoBackClick}
            onGoBackClick={onGoBackClick}
          />
          <div className={styles.localisationContainer}>
            {/* Reporting Date, Time and Numbers Format */}
            <div className={styles.reportingDate}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n(
                    'storeLocalisation.index.reportingDateTimeandNumbersFormat',
                  )}
                </div>
              </div>
              <div className={`${styles.timeZoneContent} ${styles.content}`}>
                <CommonFormItem
                  timezoneOptions={timezoneOptions}
                  disabled={!view_settings}
                  disableEdit={!edit_setting}
                  isStore={true}
                />
              </div>
            </div>
            {/* Address Format Localisation */}
            <div className={styles.addressFormat}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n('storeLocalisation.index.addressFormatLocalisation')}
                </div>
              </div>
              <div
                className={`${styles.addressFormatLocalisation} ${styles.content}`}>
                <Form.Item name={'addressFormats'}>
                  <AddressFormats disableEdit={!edit_setting} />
                </Form.Item>
                {/* <AddressFormatCustom> */}
              </div>
            </div>
            <div className={styles.addressFormat}>
              <div className={styles.header}>
                <div className={styles.title}>
                  {getI18n('storeLocalisation.index.addressAutofill')}
                </div>
              </div>
              <div className={styles.content}>
                <Form.Item
                  name={'googleAutoCompleteEnabled'}
                  initialValue={false}
                  valuePropName={'checked'}>
                  <Checkbox disabled={!edit_setting}>
                    {getI18n(
                      'storeLocalisation.index.enableGooglePlaceAutocomplete',
                    )}
                  </Checkbox>
                </Form.Item>
                <Form.Item
                  noStyle
                  shouldUpdate={(p, c) =>
                    p.googleAutoCompleteEnabled !== c.googleAutoCompleteEnabled
                  }>
                  {({ getFieldValue }) => {
                    const checked = getFieldValue('googleAutoCompleteEnabled');
                    return (
                      checked && (
                        <Form.Item
                          name={'googlePlaceKey'}
                          label={
                            <div className={styles.formItemLabel}>
                              {getI18n(
                                'storeLocalisation.index.googleplacekey',
                              )}
                            </div>
                          }
                          rules={[
                            {
                              required: true,
                              type: 'string',
                              whitespace: true,
                            },
                          ]}>
                          <Input size='large' disabled={!edit_setting} />
                        </Form.Item>
                      )
                    );
                  }}
                </Form.Item>
              </div>
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};
export default Localisation;
